import { useRef, useEffect, useState } from "react";
import PaymentService from "Services/PaymentService";
import API from "Helpers/API";
import { apiUrl } from "Constants/apiUrl";
import { Token } from "Features/CoverageApplication/Models/applicationForm.model";
import { isAfter, fromUnixTime } from "date-fns";

declare let Flex: new (config: object) => { microform: (...args) => Microform };

type MicroformHookConfig = {
  numberContainerId: string;
  securityCodeContainerId: string;
  styles?: Record<string, unknown>;
  flexScriptTagId?;
  onFieldChange?: (payload: {
    controlName: string;
    event: MicroFormFieldChangeEvent;
  }) => void;
  onFieldBlur?: (controlName: string) => void;
};

const parseJWTPayload = (jwt: string) => {
  const parts = jwt.split(".");
  const base64 = parts[1];

  return JSON.parse(atob(base64));
};

export const tokenizerErrors = {
  CS_CAPTURE_CONTEXT_EXPIRY_ERROR: "CS_CAPTURE_CONTEXT_EXPIRY_ERROR",
  CS_TOKEN_CREATION_ERROR: "CS_TOKEN_CREATION_ERROR",
};

export interface MicroFormFieldChangeEvent {
  couldBeValid: boolean;
  empty: boolean;
  valid: boolean;
}

type TokenOptions = {
  expirationMonth: string;
  expirationYear: string;
};

export type Microform = {
  createField: (...args: unknown[]) => {
    on: (...args) => void;
    load: (containerId: string) => void;
  };
  createToken: (
    options: TokenOptions,
    handler: (...args: unknown[]) => void,
  ) => void;
};

export type Tokenizer = (options: TokenOptions) => Promise<Token>;

/**
 * @description Initialized microform credit card fields
 * @returns a tokenizer function to create a Cybersource transient token for payment
 */
export const useMicroform = ({
  numberContainerId,
  securityCodeContainerId,
  onFieldChange,
  onFieldBlur,
  flexScriptTagId = "cybersource-flex-script",
  styles = {
    input: {
      "font-size": "1.125rem",
      color: "#495057",
      "-webkit-font-smoothing": "antialiased",
      "-moz-osx-font-smoothing": "grayscale",
    },
    "::placeholder": {
      "-webkit-font-smoothing": "antialiased",
      "-moz-osx-font-smoothing": "grayscale",
    },
  },
}: MicroformHookConfig): {
  tokenizer: Tokenizer;
  formLoading: boolean;
  sessionError: boolean;
} => {
  const microformRef = useRef<null | Microform>();
  const captureContextRef = useRef<null | string>();
  const stringifiedStyles = JSON.stringify(styles);
  const [formLoading, setFormLoading] = useState(true);
  const [sessionError, setSessionError] = useState(false);

  useEffect(() => {
    const paymentSrvc = new PaymentService(new API(apiUrl, false));
    paymentSrvc.getMicroformSession().then(
      ({ data: { clientLibrary, clientLibraryIntegrity, captureContext } }) => {
        const createMicroform = () => {
          // SETUP
          const flex = new Flex(captureContext);
          const microform = flex.microform("card", {
            styles: JSON.parse(stringifiedStyles),
          }) as Microform;

          // Create Fields
          const cardNumberField = microform.createField("number", {
            placeholder: "0000 0000 0000 0000",
          });

          const cvvField = microform.createField("securityCode", {
            placeholder: "XXX",
          });

          // Bind field event handlers
          [
            { controlName: "cardNumberValid", field: cardNumberField },
            { controlName: "cvvValid", field: cvvField },
          ].forEach(({ field, controlName }) => {
            field.on("change", (event: MicroFormFieldChangeEvent) => {
              onFieldChange && onFieldChange({ controlName, event });
            });
            field.on("blur", () => {
              onFieldBlur && onFieldBlur(controlName);
            });
          });

          cardNumberField.load("#card-number");
          cvvField.load("#cvv");

          microformRef.current = microform;
          captureContextRef.current = captureContext;
          setFormLoading(false);
        };

        const loadScriptAndCreateForm = () => {
          const script = document.createElement("script");
          script.id = flexScriptTagId;
          script.src = clientLibrary;
          script.type = "text/javascript";
          script.async = true;
          script.integrity = clientLibraryIntegrity;
          script.crossOrigin = "anonymous";
          // run setup code after script has loaded successfully
          script.onload = createMicroform;
          // insert to document
          document.head.appendChild(script);
        };

        if (document.getElementById(flexScriptTagId)) {
          createMicroform();
        } else {
          loadScriptAndCreateForm();
        }
      },
      () => {
        setSessionError(true);
      },
    );
  }, [
    stringifiedStyles,
    flexScriptTagId,
    numberContainerId,
    securityCodeContainerId,
    onFieldChange,
    onFieldBlur,
  ]);

  const tokenizer = (options: TokenOptions) =>
    new Promise<Token>((resolve, reject) => {
      microformRef.current.createToken(options, (err, jwtToken: string) => {
        // Check if capture context has expired
        try {
          const parsedCaptureContext = parseJWTPayload(
            captureContextRef.current,
          );

          const { exp } = parsedCaptureContext;
          const now = new Date();

          if (isAfter(now, fromUnixTime(exp))) {
            reject(tokenizerErrors.CS_CAPTURE_CONTEXT_EXPIRY_ERROR);
            return;
          }
        } catch {
          console.warn("Capture Context Expiration Validation Failure");
        }

        if (err) {
          reject(tokenizerErrors.CS_TOKEN_CREATION_ERROR);
        } else {
          let token: Token;

          try {
            const parsed = parseJWTPayload(jwtToken);
            const { maskedValue: maskedPan, detectedCardTypes } =
              parsed.content.paymentInformation.card.number;

            token = {
              token: jwtToken,
              maskedPan,
              cardType: detectedCardTypes[0],
              expiry: parsed.exp,
            };
            resolve(token);
          } catch {
            reject(tokenizerErrors.CS_TOKEN_CREATION_ERROR);
          }
        }
      });
    });

  return { tokenizer, formLoading, sessionError };
};
